import { FieldValue, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ADDRESS_1,
  ADDRESS_2,
  ZIPCODE,
  STATE,
  CITY,
  COMMUNITY_ID,
  COUNTRY,
  NAME,
  CONTACT_NAME,
  EMAIL_ID,
  WEBSITE,
  PHONE_NO,
  IS_ACTIVE,
  FEIN,
} from "./consts";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "@/components/Toast";
import {
  useUpdateVendorMutation,
  useGetVendorById,
  useAddVendorMutation,
} from "@/services";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { schema } from "./validations";
import { routes } from "@/routes/utils";

const useStep1 = ({ onNextClick }: { onNextClick?: () => void }) => {
  const { vendorId, id, action } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const communityData = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const isMasterData = location?.pathname?.toLowerCase()?.includes("master-data");
  const communityId = isMasterData ? communityData?.communityId : id;
  const vendorListPath = isMasterData
    ? `/${routes.admin}/${routes.masterData}/${routes.vendorMgmt}`
    : `/${routes.admin}/${routes.adminConsole}/${routes.communities}/${id}/${routes.vendorMgmt}`;

  const form = useForm<any>({
    defaultValues: {
      [NAME]: "",
      [CONTACT_NAME]: "",
      [EMAIL_ID]: "",
      [WEBSITE]: "",
      [PHONE_NO]: "",
      [IS_ACTIVE]: true,
      [FEIN]: "",
      [ADDRESS_1]: "",
      [ADDRESS_2]: "",
      [STATE]: "",
      [CITY]: "",
      [ZIPCODE]: "",
      [COUNTRY]: "USA",
      [COMMUNITY_ID]: communityData?.displayId,
    },
    resolver: zodResolver(schema),
  });

  const stateList = useSelector(
    (state: RootState) => state.currentData.stateData
  );
  const [stateKey, setStateKey] = useState<number>(Math.random() * 3);

  const isView = action === "view";

  const requiredFields = getRequiredFields(schema);

  const { data: details, isLoading: isVendorDtlLoading } = useGetVendorById({
    id: vendorId as string,
    queryParams: {
      enabled: !!vendorId,
    },
  });

  useEffect(() => {
    if (details) {
      form.reset({
        [NAME]: details?.[NAME]?.toString() || "",
        [CONTACT_NAME]: details?.[CONTACT_NAME]?.toString() || "",
        [EMAIL_ID]: details?.[EMAIL_ID]?.toString() || "",
        [WEBSITE]: details?.[WEBSITE]?.toString() || "",
        [PHONE_NO]: details?.[PHONE_NO]?.toString() || "",
        [IS_ACTIVE]: details?.[IS_ACTIVE],
        [FEIN]: details?.[FEIN]?.toString() || "",
        [ADDRESS_1]: details?.[ADDRESS_1]?.toString() || "",
        [ADDRESS_2]: details?.[ADDRESS_2]?.toString() || "",
        [STATE]: details?.[STATE]?.toString() || "",
        [CITY]: details?.[CITY]?.toString() || "",
        [ZIPCODE]: details?.[ZIPCODE]?.toString() || "",
      });
      setStateKey(Math.random() * 3);
    }
  }, [details]);

  const { mutate: addInfo, isPending: isAddPending } = useAddVendorMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
      return error?.response?.data?.message;
    },
    onSuccess: (data: any) => {
      navigate(
        `${vendorListPath}/${data?.data?.vendorId}/add`,
        { state: { step: 2 } }
      );
      toast.success("Vendor Info Added Successfully");
    },
  });

  // update property
  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateVendorMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        onNextClick && onNextClick();
        toast.success("Vendor Info Updated Successfully");
      },
    });

  const onSubmit = (data: FieldValue<any>) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);

    if (vendorId) {
      updateInfo({ id: vendorId, data: { ..._data, [COMMUNITY_ID]: communityId } });
    } else {
      addInfo({ data: { ..._data, [COMMUNITY_ID]: communityId } });
    }
  };

  const formatFein = (value: string) => {
    // Remove non-alphanumeric characters
    const raw = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

    // Format as XX-XXXXXXX (2 letters, hyphen, 7 digits)
    const part1 = raw.slice(0, 2);
    const part2 = raw.slice(2, 9);
    return `${part1}${part2 ? "-" + part2 : ""}`;
  };

  return {
    form,
    onSubmit,
    requiredFields,
    isAddPending,
    isUpdatePending,
    isView,
    stateList,
    stateKey,
    vendorId,
    isVendorDtlLoading,
    formatFein,
  };
};

export default useStep1;
